import { FunctionalityEnum, UserRoleEnum } from '@elearning-platform/api-types/enums';
import { UserInfoInterface } from '@elearning-platform/api-types/interfaces';
import { useNotifications, UseNotifications } from '@elearning-platform/ui';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { Location, NavigateFunction, NavigateOptions, useLocation, useNavigate } from 'react-router';
import { Dispatch } from 'redux';

import { setAuthTokenTyped } from '../api/api';
import { setAuthToken } from '../api/default';
import { getUserInfo, logoutRequest, updateAssetsSession } from '../api/requests';
import { AppSettingsActions } from '../appSettings/redux/appSettings.reducer';
import { AuthContext } from '../contexts';
import { AuthReducerAction, KnowledgeBaseViewMode, LocalStorageKey, QueryKey, RouteNameEnum } from '../enums';
import { clearApiCache } from '../helpers/clearApiCache';
import { AppSettings, LangRouteInfo, RouteInfoRequiredFunctionalitiesGroups, UseSocket } from '../interfaces';
import { UseActionsAfterSignIn, UseAuth, UseRedirectTo, UseVolatileSettings } from '../interfaces/hooks';
import { CommonActions } from '../redux';
import i18n from '../translations/i18n';
import { LoginResponse } from '../types/api';
import { AuthContextData, UseLocalStorage } from '../types/internal';
import { UserContextActions } from '../userContext';
import { UserRoleName } from '../userContext/userContext.constants';
import { getRouteNameByUrl, getUrlByRouteName } from '../utils';

import { useActionsAfterSignIn } from './useActionsAfterSignIn.hook';
import { useLocalStorage } from './useLocalStorage.hook';
import { useRedirectTo } from './useRedirectTo.hook';
import { useSocket } from './useSocket.hook';
import { useVolatileSettings } from './useVolatileSettings.hook';

export const useAuth: () => UseAuth = (): UseAuth => {
  const dispatch: Dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { pathname, state }: Location = useLocation();
  const queryClient: QueryClient = useQueryClient();
  const [authContextState, dispatchAuthContext]: AuthContextData = useContext(AuthContext);
  const [, storeAuthorizationToken]: UseLocalStorage<string | null> = useLocalStorage<string | null>(
    LocalStorageKey.AuthorizationToken, null
  );
  const [, storeAppSettings]: UseLocalStorage<AppSettings | null> = useLocalStorage<AppSettings | null>(
    LocalStorageKey.AppSettings, null
  );
  const [, storeSelectedBrand]: UseLocalStorage<number | null> = useLocalStorage<number | null>(
    LocalStorageKey.SelectedBrand, null
  );
  const { actionsAfterSignIn }: UseActionsAfterSignIn = useActionsAfterSignIn();
  const { changeAppLoadingScreenVisible }: UseVolatileSettings = useVolatileSettings();
  const { clearAll }: UseNotifications = useNotifications();
  const setTokenAfterSignIn: (token: string) => void = useCallback((token: string): void => {
    dispatchAuthContext({ type: AuthReducerAction.UpdateAuthToken, payload: token });
    setAuthTokenTyped(token);
    setAuthToken(token);
    storeAuthorizationToken(token);
    void updateAssetsSession();
  }, [dispatchAuthContext, storeAuthorizationToken]);
  const { socket }: UseSocket = useSocket();

  const signIn: (
    loginResponse: LoginResponse, token: string
  ) => void = (
    loginResponse: LoginResponse, token: string
  ): void => {
    dispatch(UserContextActions.setLoading(true));
    dispatch(UserContextActions.resetUserData());
    setTokenAfterSignIn(token);
    dispatchAuthContext({
      type: AuthReducerAction.SignIn,
      payload: {
        token,
        userData: loginResponse.user,
        appSettings: {
          currentMenuRole: loginResponse.user.role.name,
          passwordExpirationInfo: loginResponse.passwordExpirationInfo,
        },
      },
    });
    queryClient.setQueryData<UserInfoInterface>([QueryKey.UserInfo], loginResponse.user);
    void i18n.changeLanguage(loginResponse.user.language);
    dispatch(UserContextActions.tryLoginSuccess(loginResponse));
    if (hasRequiredFunctionalities([FunctionalityEnum.ApiNotificationList], loginResponse.user.availableRoutes)) {
      dispatch(CommonActions.fetchNotifications());
    }
    if (loginResponse.user.role.name === UserRoleName.Onboarding) {
      redirect({ route: RouteNameEnum.Onboarding });
    } else {
      const pathnameBeforeSignIn: RouteNameEnum | undefined = state?.from?.pathname
        ? getRouteNameByUrl(state?.from?.pathname)
        : undefined;
      const route: LangRouteInfo | undefined = pathnameBeforeSignIn
        ? getUrlByRouteName(pathnameBeforeSignIn)
        : undefined;

      if (
        pathnameBeforeSignIn
        && route
        && hasRequiredFunctionalities(route.requiredFunctionalities, loginResponse.user.availableRoutes)
      ) {
        navigate(state?.from?.pathname);
      } else {
        const availableRoutesKeys: RouteNameEnum[] = Object.values(RouteNameEnum)
          .filter((item: string): boolean => {
            const route: LangRouteInfo | undefined = getUrlByRouteName(item as RouteNameEnum);

            return !!route
              && route.secured
              && hasRequiredFunctionalities(route.requiredFunctionalities, loginResponse.user.availableRoutes)
              && item !== RouteNameEnum.NotFound;
          });
        if (availableRoutesKeys.includes(RouteNameEnum.Dashboard)) {
          redirect({ route: RouteNameEnum.Dashboard });
        } else {
          redirect({ route: availableRoutesKeys[0] });
        }
      }
    }
    dispatch(UserContextActions.setLoading(false));
    actionsAfterSignIn(loginResponse);
  };

  const changeKnowledgeBaseViewMode: (mode: KnowledgeBaseViewMode) => void = useCallback(
    (mode: KnowledgeBaseViewMode): void =>
      dispatchAuthContext({ type: AuthReducerAction.UpdateAppSettings, payload: { knowledgeBaseViewMode: mode } }),
    [dispatchAuthContext]
  );

  const signOutCleanup: (withLocationStateSave?: boolean) => void = useCallback((withLocationStateSave: boolean = false): void => {
    const currentPathname: string = location.pathname;
    dispatch(AppSettingsActions.clearData());
    dispatch(UserContextActions.setLoggedOutSuccess());
    dispatchAuthContext({ type: AuthReducerAction.SignOut, payload: null });
    storeAuthorizationToken(null);
    storeAppSettings(null);
    storeSelectedBrand(null);
    setAuthTokenTyped(null);
    setAuthToken(null);
    setTimeout((): void => {
      clearApiCache();
      queryClient.clear();
      const options: NavigateOptions = {
        state: {
          from: { pathname: currentPathname },
        },
        replace: true,
      };
      navigate(getUrlByRouteName(RouteNameEnum.SignIn)?.url ?? '', withLocationStateSave ? options : {});
      changeAppLoadingScreenVisible(false);
    }, 0);
    socket?.close();
    socket?.offAny();
    clearAll();
  }, [
    changeAppLoadingScreenVisible,
    dispatch,
    dispatchAuthContext,
    clearAll,
    queryClient,
    storeAppSettings,
    storeAuthorizationToken,
    storeSelectedBrand,
    socket,
    navigate,
  ]);

  const signOut: () => void = useCallback((): void => {
    changeAppLoadingScreenVisible(true);
    signOutCleanup();
    void logoutRequest();
  }, [changeAppLoadingScreenVisible, signOutCleanup]);

  const isFunctionalityEnumArray: (
    value: FunctionalityEnum[] | RouteInfoRequiredFunctionalitiesGroups
  ) => value is FunctionalityEnum[] = (
    value: FunctionalityEnum[] | RouteInfoRequiredFunctionalitiesGroups
  ): value is FunctionalityEnum[] => (
    Array.isArray(value)
      && value.every((item: unknown): boolean => typeof item === 'string')
  );

  const hasRequiredFunctionalities: (
    requiredFunctionalities: FunctionalityEnum[] | RouteInfoRequiredFunctionalitiesGroups,
    availableRoutes: Record<FunctionalityEnum, boolean>
  ) => boolean = useCallback(
    (
      requiredFunctionalities: FunctionalityEnum[] | RouteInfoRequiredFunctionalitiesGroups,
      availableRoutes: Record<FunctionalityEnum, boolean>
    ): boolean => {
      if (isFunctionalityEnumArray(requiredFunctionalities)) {
        return requiredFunctionalities
          .every((functionalityKey: FunctionalityEnum): boolean => !!Object.entries(availableRoutes)
            .find(([routeName, isRouteAvailable]: [string, boolean]): boolean => routeName === functionalityKey && isRouteAvailable)
          );
      } else {
        return requiredFunctionalities.functionalities.some(
          (functionalityKeyArray: FunctionalityEnum[]): boolean => functionalityKeyArray.every(
            (functionalityKey: FunctionalityEnum): boolean => !!Object.entries(availableRoutes).find(
              ([routeName, isRouteAvailable]: [string, boolean]): boolean => routeName === functionalityKey && isRouteAvailable
            )
          )
        );
      }
    },
    []
  );

  const hasUserRequiredFunctionalities: (
    requiredFunctionalities: FunctionalityEnum[] | RouteInfoRequiredFunctionalitiesGroups
  ) => boolean = useCallback(
    (
      requiredFunctionalities: FunctionalityEnum[] | RouteInfoRequiredFunctionalitiesGroups
    ): boolean => {
      if (!authContextState.userData) {
        return false;
      }

      return hasRequiredFunctionalities(requiredFunctionalities, authContextState.userData.availableRoutes);
    },
    [hasRequiredFunctionalities, authContextState.userData]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isRouteAvailableForMenuRole: (routeNameKey: string, menuRole: UserRoleEnum) => boolean = useCallback((
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    routeNameKey: string, menuRole: UserRoleEnum
  // separated menu not available on vwfs
  ): boolean => true, []);

  const changeCurrentMenuRole: (menuRole: UserRoleEnum) => void = useCallback((menuRole: UserRoleEnum): void => {
    const currentRoute: RouteNameEnum | undefined = getRouteNameByUrl(pathname);
    if (currentRoute && !isRouteAvailableForMenuRole(currentRoute, menuRole)) {
      redirect({ route: RouteNameEnum.Dashboard });
    }
    dispatchAuthContext({ type: AuthReducerAction.UpdateAppSettings, payload: { currentMenuRole: menuRole } });
  }, [pathname, dispatchAuthContext, isRouteAvailableForMenuRole, redirect]);

  const forceUserInfoCacheReload: () => Promise<void> = useCallback(async (): Promise<void> => {
    const response: UserInfoInterface | undefined = await getUserInfo().catch((): undefined => undefined);
    if (response) {
      queryClient.setQueryData<UserInfoInterface>([QueryKey.UserInfo], response);
    }
  }, [queryClient]);

  return {
    signIn,
    signOut,
    signOutCleanup,
    hasUserRequiredFunctionalities,
    userToken: authContextState.token,
    userData: authContextState.userData,
    appSettings: authContextState.appSettings,
    isLoggedIn: !!authContextState.userData,
    isAuthInitialized: authContextState.isAuthInitialized,
    changeCurrentMenuRole,
    isRouteAvailableForMenuRole,
    changeKnowledgeBaseViewMode,
    setTokenAfterSignIn,
    forceUserInfoCacheReload,
  };
};
